import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import AddIcon from '@material-ui/icons/Add';
import MaterialTable from 'material-table';
import { Alert, Button, ButtonToolbar, Collapse } from 'reactstrap';
import { Field, reduxForm } from 'redux-form';
import { withTranslation } from 'react-i18next';
import { Dialog, IconButton, Tooltip } from '@material-ui/core';
import validate from '../Validate';
import SmartCard from '../../../../shared/components/SmartCard';
import renderField from '../../../../shared/components/form/RenderField';
import { percentageMasker } from '../../../../shared/components/form/Masks';
import materialTableIcons from '../../../../shared/utils/materialTableIcons';
import MaterialTableThemeProvider from '../../../../shared/components/MaterialTableThemeProvider';

const DiscontConfiguration = (props) => {
  const { t, initialize, discountConfigurations, onConfirm, titleId } = props;

  const [dialog, setDialog] = useState(false);
  const [advancePayment, setAdvancePayment] = useState({
    id: 0,
    titleId,
    daysBeforeExpiration: 0,
    discountPercentage: 1,
  });
  const [notificationAlert, setNotificationAlert] = useState({
    status: false,
    title: '',
    color: '',
  });

  const [discountsList, setDiscountsList] = useState([]);

  const columns = () => {
    return [
      { title: t('general.before-dueDate'), field: 'daysBeforeExpiration' },
      { title: t('general.percentage-discount'), field: 'discountPercentage' },
    ];
  };

  const toggleDialog = () => {
    setDialog(!dialog);
  };

  const advancePaymentDaysBeforeChange = (evt) => {
    if (evt) {
      const updatedFilter = { ...advancePayment };
      const strValue = evt.target.value.replace(',', '');
      const newValue = parseInt(strValue, 10);
      if (!Number.isNaN(newValue)) {
        updatedFilter[evt.target.name] = parseFloat(newValue);
      }
      setAdvancePayment(updatedFilter);
    }
  };

  const advancePaymentDiscountChange = (evt) => {
    if (evt) {
      const updatedFilter = { ...advancePayment };
      const strValue = evt.target.value.replace(',', '');
      const newValue = parseFloat(parseInt(strValue, 10) / 100).toFixed(2);
      if (!Number.isNaN(newValue)) {
        updatedFilter[evt.target.name] = parseFloat(newValue);
      }
      setAdvancePayment(updatedFilter);
    }
  };

  const removeItemClickHandler = (evt, rowData) => {
    const found = discountsList.findIndex((element) => element === rowData);
    const updated = [...discountsList];
    updated.splice(found, 1);
    setDiscountsList(updated);
  };

  const validateAdvanceToAdd = (advanceToAdd) => {
    const itemFound = discountsList.find(
      (item) => item.daysBeforeExpiration === advanceToAdd.daysBeforeExpiration
    );
    if (itemFound) {
      return false;
    }
    return true;
  };

  const onShowAlert = (color, title, message) => {
    setNotificationAlert({
      ...notificationAlert,
      status: true,
      title,
      color,
      message,
    });
    window.setTimeout(() => {
      setNotificationAlert({
        ...notificationAlert,
        status: false,
        title: '',
        color: '',
        message: '',
      });
    }, 5000);
  };

  const onAddAdvancePaymentClick = () => {
    const updated = [...discountsList, advancePayment];
    const advanceToAdd = advancePayment;
    const valid = validateAdvanceToAdd(advanceToAdd);

    if (discountsList.length < 3) {
      if (
        advanceToAdd.daysBeforeExpiration < 0 ||
        advanceToAdd.daysBeforeExpiration > 99
      ) {
        onShowAlert(
          'warning',
          'errors.title',
          'answers.validation.must-be-between'
        );
      } else if (
        advanceToAdd.discountPercentage <= 0 ||
        advanceToAdd.discountPercentage > 99
      ) {
        onShowAlert(
          'warning',
          'errors.title',
          'answers.validation.discount-greater-than-zero'
        );
      } else if (valid) {
        setDiscountsList(updated);
      } else {
        onShowAlert(
          'warning',
          'errors.title',
          'answers.validation.already-registered'
        );
      }
    } else {
      onShowAlert(
        'warning',
        'errors.title',
        'answers.validation.prepayment-discount'
      );
    }
  };

  const compareOnConfirm = () => {
    const originalList = discountConfigurations.map((item) => {
      if (item.tableData) {
        const { tableData, ...itemRef } = item;
        return itemRef;
      }
      return item;
    });
    const newList = discountsList.map((item) => {
      if (item.tableData) {
        const { tableData, ...itemRef } = item;
        return itemRef;
      }
      return item;
    });

    let changed = false;
    if (newList.length === originalList.length) {
      originalList.forEach((itemA) => {
        const itemIndex = newList.findIndex(
          (intemB) =>
            itemA.daysBeforeExpiration === intemB.daysBeforeExpiration &&
            itemA.discountPercentage === intemB.discountPercentage
        );
        if (itemIndex === -1) {
          changed = true;
        }
      });
    } else {
      changed = true;
    }
    toggleDialog();
    onConfirm(newList, changed);
  };

  useEffect(() => {
    setDiscountsList(discountConfigurations);
    return () => {
      setDiscountsList([]);
    };
  }, []);

  useEffect(() => {
    initialize(advancePayment);
  }, [advancePayment]);

  return (
    <>
      <Dialog
        className="m-2 align-items-end"
        style={{ bottom: 'auto' }}
        open={dialog}
      >
        <div
          className="modal-dialog--warning modal-alert d-flex flex-column justify-content-center"
          style={{ height: '16rem' }}
        >
          <div className="modal__header d-flex flex-column align-items-center">
            <button
              className="lnr lnr-cross modal__close-btn"
              type="button"
              onClick={toggleDialog}
            />
            <span className="lnr lnr-flag modal__title-icon" />
            <h4 className="bold-text  modal__title">{t('warnings.title')}</h4>
          </div>
          <div className="modal__body text-center">
            {t('answers.validation.applying-this-setting')}
          </div>

          <ButtonToolbar className="modal__footer">
            <Button onClick={toggleDialog}>{t('general.cancel')}</Button>{' '}
            <Button
              color="warning"
              onClick={() => {
                compareOnConfirm();
              }}
            >
              {t('general.confirm')}
            </Button>
          </ButtonToolbar>
        </div>
      </Dialog>
      <SmartCard>
        <form className="form">
          <div className="form__form-group--3_columns">
            <div className="form__form-group">
              <span className="form__form-group-label">
                {t('general.before-dueDate')}
              </span>
              <div className="form__form-group-field">
                <Field
                  name="daysBeforeExpiration"
                  type="number"
                  component={renderField}
                  min="0"
                  max="99"
                  onBlur={(evt) => advancePaymentDaysBeforeChange(evt)}
                />
              </div>
            </div>
            <div className="form__form-group">
              <span className="form__form-group-label">
                {t('general.discount')}
              </span>
              <div className="form__form-group-field">
                <Field
                  name="discountPercentage"
                  type="text"
                  component={renderField}
                  onBlur={(evt) => advancePaymentDiscountChange(evt)}
                  {...percentageMasker()}
                />
              </div>
            </div>
            <div className="d-flex flex-grow-1 align-items-center pl-2">
              <Tooltip title={t('general.add-discount')}>
                <IconButton
                  className="icon-button"
                  aria-label="add"
                  onClick={onAddAdvancePaymentClick}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </form>
        <Collapse isOpen={notificationAlert.status}>
          <Alert color={notificationAlert.color}>
            {t(notificationAlert.message)}
          </Alert>
        </Collapse>
        <MaterialTableThemeProvider>
          <div className="form__form-group">
            <MaterialTable
              data={discountsList}
              columns={columns()}
              icons={materialTableIcons}
              actions={[
                {
                  icon: 'delete',
                  tooltip: t('general.tables.delete_registry'),
                  onClick: (evt, rowData) =>
                    removeItemClickHandler(evt, rowData),
                },
              ]}
              options={{
                paging: false,
                showTitle: false,
                search: false,
                toolbar: false,
                actionsColumnIndex: 8,
              }}
              localization={{
                pagination: {
                  labelRowsSelect: t('general.tables.rows'),
                  labelDisplayedRows: `{from}-{to} ${t(
                    'general.tables.labelDisplayRows'
                  )} {count}`,
                  firstTooltip: t('general.tables.firstTooltip'),
                  previousTooltip: t('general.tables.firstTooltip'),
                  nextTooltip: t('general.tables.firstTooltip'),
                  lastTooltip: t('general.tables.firstTooltip'),
                },

                header: {
                  actions: t('general.tables.actions'),
                },
                toolbar: {
                  exportTitle: t('general.tables.export_title'),
                  exportAriaLabel: t('general.tables.export'),
                  exportCSVName: t('general.tables.exportCSVName'),
                  exportPDFName: t('general.tables.exportPDFName'),
                  searchPlaceholder: t('general.tables.search_placeholder'),
                  nRowsSelected: `{0} ${t('general.tables.nRowsSelected')}`,
                },
                body: {
                  emptyDataSourceMessage: t(
                    'general.tables.no-data-to-display'
                  ),
                  exportTitle: t('general.tables.export_title'),
                  exportAriaLabel: t('general.export'),
                  exportCSVName: t('general.tables.exportCSVName'),
                  exportPDFName: t('general.exportPDFName'),
                  searchPlaceholder: t('general.tables.search_placeholder'),
                },
              }}
            />
          </div>
        </MaterialTableThemeProvider>
        <ButtonToolbar className="form__button-toolbar">
          <Button color="success" onClick={toggleDialog}>
            {t('general.save')}
          </Button>
        </ButtonToolbar>
      </SmartCard>
    </>
  );
};

DiscontConfiguration.propTypes = {
  t: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  titleId: PropTypes.number.isRequired,
  discountConfigurations: PropTypes.arrayOf(PropTypes.shape(PropTypes.shape))
    .isRequired,
};

export default reduxForm({
  form: 'AdvancePayment',
  validate,
})(withTranslation('common')(DiscontConfiguration));
